import type { Identifier, XYCoord } from 'dnd-core';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DragSourceMonitor, useDrag, useDrop } from 'react-dnd';
import { PostItemFragment } from '../../../../models/graphql';
import { CardOrientationEnum } from '../../../../components/cards/BasePostCard/BasePostCard.view';
import classes from './DraggablePostCard.module.scss';
import classNames from 'classnames';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { ContainerListItem } from '../../../../components/actions/ListItem/ListItem.view';
import { UseFormReturn } from 'react-hook-form';
import { useDimensions } from '../../../../hooks/useDimensions';
import { DraggableTypesObject } from '../../../../constants/drag-and-drop';
import { PostScheduleViewContext } from '../PostSchedule.controller';
import { BreakpointsValuesEnum } from 'constants/breakpoints-values';
import { BasePostCardLandscapeView } from '../../../../components/cards/BasePostCard/BasePostCardLandscape.view';

export type DraggablePostCardProps = {
  id: string;
  index: number;
  useFormInstance?: UseFormReturn;
  post: PostItemFragment;
  movePost: (dragId: string, hoverIndex: number | undefined) => void;
  cancelMove: () => void;
  endMove: () => void;
  menuListItems: ContainerListItem[];
  animate?: boolean;
};

interface DragItem {
  index: number;
  id: string;
  type: string;
}

export const DraggablePostCard = (props: DraggablePostCardProps): JSX.Element => {
  const { id, post, index, animate, movePost, cancelMove, endMove, menuListItems, useFormInstance } = props;
  const cardRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<HTMLDivElement>(null);
  const [moveCalled, setMoveCalled] = useState<boolean>(false);
  const bodyRef = useRef(document.body);
  const { width } = useDimensions(bodyRef);

  const [{ handlerId }, drop] = useDrop<DragItem, void, { handlerId: Identifier | null }>({
    accept: DraggableTypesObject.POST_CARD,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId()
      };
    },
    hover(item: DragItem, monitor) {
      if (!cardRef.current) {
        return;
      }

      const dragIndex = item.index;
      const hoverIndex = index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // Determine rectangle on screen
      const hoverBoundingRect = cardRef.current?.getBoundingClientRect();

      // Get vertical middle
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      // Determine mouse position
      const clientOffset = monitor.getClientOffset();

      // Get pixels to the top
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }
      // Use itemId as index can change if we are fetching sub-queue
      const dragId = item.id;

      // Time to actually perform the action
      movePost(dragId, hoverIndex);

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex;
    }
  });

  const [{ isDragging }, drag, preview] = useDrag({
    type: DraggableTypesObject.POST_CARD,
    item: () => {
      return {
        id,
        index,
        post,
        orientation: CardOrientationEnum.LANDSCAPE,
        width: handleRef.current?.offsetWidth || 0,
        height: handleRef.current?.offsetHeight || 0,
        cardRef: cardRef.current
      };
    },
    collect: (monitor: DragSourceMonitor) => ({
      isDragging: monitor.isDragging(),
      handlerId: monitor.getHandlerId()
    }),
    end: (_, monitor) => (monitor.didDrop() ? endMove() : cancelMove())
  });

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: false });
  }, [preview]);

  const draggablePostCardClasses = classNames(classes.DraggablePostCard);
  const orientation = useMemo(() => {
    return width < BreakpointsValuesEnum.tabletportrait ? CardOrientationEnum.PORTRAIT : CardOrientationEnum.LANDSCAPE;
  }, [width]);

  const dragStateClasses = classNames({
    [classes.DragStatePlaceholder]: isDragging
  });

  useEffect(() => {
    if (isDragging && !moveCalled) {
      movePost(id, undefined);
      setMoveCalled(() => true);
    }
  }, [isDragging, movePost, id, moveCalled]);

  drop(cardRef); // Make the entire card the drop target
  drag(handleRef); // Restrict dragging initiation to the handle

  return (
    <div className={dragStateClasses} ref={cardRef}>
      <div className={draggablePostCardClasses}>
        <BasePostCardLandscapeView
          ref={handleRef}
          handlerId={handlerId}
          index={index}
          context={PostScheduleViewContext.LIST}
          post={post}
          isDraggable={false}
          orientation={orientation}
          hasHoverBackground={false}
          animateCard={animate}
          {...(useFormInstance ? { hideCheckbox: false, useFormInstance } : { hideCheckbox: true })}
          menuListItems={menuListItems}
        />
      </div>
    </div>
  );
};
